import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, Calendar, MapPin, Tag, X } from 'lucide-react';
import api from '../api';
import EventCard from '../components/EventCard';
import SEO from '../components/SEO';

export default function EventsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [events, setEvents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

    useEffect(() => {
        loadCategories();
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadEvents();
    }, [searchParams]);

    const loadCategories = async () => {
        try {
            const cats = await api.getCategories();
            setCategories(cats);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadEvents = async () => {
        setLoading(true);
        try {
            const params = {};
            const category = searchParams.get('category');
            const searchQuery = searchParams.get('search');

            if (category) params.category = category;
            if (searchQuery) params.search = searchQuery;

            const data = await api.getEvents(params);
            setEvents(data);
        } catch (error) {
            console.error('Error loading events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams(searchParams);
        if (search) {
            params.set('search', search);
        } else {
            params.delete('search');
        }
        setSearchParams(params);
    };

    const handleCategoryFilter = (category) => {
        const params = new URLSearchParams(searchParams);
        if (category === selectedCategory) {
            params.delete('category');
            setSelectedCategory('');
        } else {
            params.set('category', category);
            setSelectedCategory(category);
        }
        setSearchParams(params);
    };

    const clearFilters = () => {
        setSearch('');
        setSelectedCategory('');
        setSearchParams({});
    };

    return (
        <main className="main">
            <SEO 
                title="Events"
                description="Browse upcoming Indian Heritage and Arts events in Singapore. Find cultural performances, workshops, exhibitions, and community gatherings celebrating Indian traditions."
                keywords="Indian events Singapore, cultural events, Indian Heritage performances, Indian arts Singapore, cultural workshops, heritage events, community gatherings"
            />
            {/* Gradient Banner at Top */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)',
                height: '120px',
                marginBottom: '32px'
            }} />

            <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
                <div style={{ marginBottom: '32px' }}>
                    <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>
                        Discover events happening in Singapore
                    </h1>
                    <p style={{ color: 'var(--text-secondary)' }}>
                        Discover Singapore's Indian Heritage and Arts events happening in Singapore
                    </p>
                </div>

                {/* Search & Filters */}
                <div className="search-filters">
                    <form onSubmit={handleSearch} className="search-input">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search events..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </form>

                    <div className="filter-buttons">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
                                onClick={() => handleCategoryFilter(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                        {(selectedCategory || search) && (
                            <button className="filter-btn" onClick={clearFilters}>
                                Clear All
                            </button>
                        )}
                    </div>
                </div>

                {/* Results */}
                {loading ? (
                    <div className="events-grid">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="skeleton" style={{ height: '400px' }} />
                        ))}
                    </div>
                ) : events.length > 0 ? (
                    <>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
                            {events.length} events found
                            {selectedCategory && ` in ${selectedCategory}`}
                            {search && ` matching "${search}"`}
                        </p>
                        <div className="events-grid">
                            {events.map((event) => (
                                <EventCard key={event.id} event={event} />
                            ))}
                        </div>
                    </>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '80px 24px',
                        background: 'var(--bg-card)',
                        borderRadius: '16px',
                        border: '1px solid var(--border)'
                    }}>
                        <Filter size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
                        <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>No events found</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                            Try adjusting your search or filters
                        </p>
                        <button className="btn btn-secondary" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
}

